import React from 'react';
import { Box, Typography, Grid, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import MonthlyDonationProgramCard from '../../../components/common/MonthlyDonationProgramCard';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCmsHomeAction } from '../../../store/slices/cmsSlice';
import { fetchMonthlyDonationsAction } from '../../../store/slices/monthlyDonationSlice';

const MonthlyDonationProgram = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px
  const isMediumScreen = useMediaQuery('(min-width: 600px) and (max-width: 835px)'); // 600-835px with scroll
  const isTablet = useMediaQuery('(min-width: 835px) and (max-width: 950px)'); // 835-950px with grid
  const isDesktop = useMediaQuery('(min-width: 950px)'); // >950px

  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const { list: monthlyDonations, loading: monthlyDonationsLoading } = useSelector((state) => state.monthlyDonation);
  const hasFetchedRef = React.useRef(false); // Prevent multiple fetches
  const isFetchingRef = React.useRef(false); // Prevent concurrent fetches

  React.useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  React.useEffect(() => {
    // If data already exists, mark as fetched and return
    if (monthlyDonations.length > 0) {
      hasFetchedRef.current = true;
      isFetchingRef.current = false;
      return;
    }

    // If already fetching or already fetched, don't fetch again
    if (hasFetchedRef.current || isFetchingRef.current || monthlyDonationsLoading) {
      return;
    }

    // Fetch only if no data exists and not currently loading
    if (monthlyDonations.length === 0 && !monthlyDonationsLoading) {
      hasFetchedRef.current = true;
      isFetchingRef.current = true;
      dispatch(fetchMonthlyDonationsAction()).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, monthlyDonations.length, monthlyDonationsLoading]);

  const { sectionTitle, sectionDescription } = React.useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const sec = data.find((s) => s?.id === 81);
    return {
      sectionTitle: sec?.title?.trim() || '',
      sectionDescription: sec?.description?.trim() || '',
    };
  }, [cmsHome]);

  // Responsive styles function
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          width: '100%',
          // borderRadius: '16px',
          paddingTop: '20px',
          paddingBottom: '20px',
          paddingLeft: '16px',
          paddingRight: '16px'
        },
        title: {
          fontSize: '18px',
          lineHeight: '16.18px',
          letterSpacing: '-2%',
          marginBottom: '10px',
          paddingLeft: '0px'
        },
        description: {
          fontSize: '10px',
          lineHeight: '19.42px',
          letterSpacing: '-2%',
          marginBottom: '20px',
          paddingLeft: '0px'
        },
        sectionHeader: {
          fontSize: '10px',
          lineHeight: '100%',
          letterSpacing: '2px',
          marginLeft: '0px',
          marginBottom: '10px'
        }
      };
    } else if (isMediumScreen) {
      return {
        container: {
          width: '94.44%',
          borderRadius: '20px',
          paddingTop: '24px',
          paddingBottom: '30px',
          paddingLeft: '20px',
          paddingRight: '20px'
        },
        title: {
          fontSize: '26px',
          marginBottom: '8px',
          paddingLeft: '0px'
        },
        description: {
          fontSize: '14px',
          lineHeight: '21px',
          marginBottom: '28px',
          paddingLeft: '0px'
        },
        sectionHeader: {
          fontSize: '13px',
          marginLeft: '0px',
          marginBottom: '13px'
        }
      };
    } else if (isTablet) {
      return {
        container: {
          width: '94.44%',
          borderRadius: '24px',
          paddingTop: '28px',
          paddingBottom: '40px',
          paddingLeft: '24px',
          paddingRight: '24px'
        },
        title: {
          fontSize: '30px',
          marginBottom: '8px',
          paddingLeft: '0px'
        },
        description: {
          fontSize: '15px',
          lineHeight: '22px',
          marginBottom: '32px',
          paddingLeft: '0px'
        },
        sectionHeader: {
          fontSize: '13px',
          marginLeft: '0px',
          marginBottom: '14px'
        }
      };
    } else {
      return {
        container: {
          width: '94.44%',
          borderRadius: '36px',
          paddingTop: '35px',
          paddingBottom: '50px',
          // paddingLeft: '40px',
          // paddingRight: '40px'
        },
        title: {
          fontSize: '36px',
          marginBottom: '10px',
          paddingLeft: '40px'
        },
        description: {
          fontSize: '16px',
          lineHeight: '24px',
          marginBottom: '40px',
          paddingLeft: '40px'
        },
        sectionHeader: {
          fontSize: '14px',
          marginLeft: '40px',
          marginBottom: '16px'
        }
      };
    }
  };

  const styles = getResponsiveStyles();

  return (
    <Box
      id="monthly-donation-programs"
      sx={{
        ...styles.container,
        maxWidth: '1440px',
        background: '#F7F7F7',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: isMobile ? '20px' : '40px',
        marginBottom: isMobile ? '30px' : '60px',
      }}
    >
      {/* JOIN MONTH */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: isMobile ? '8px' : '10px', 
        alignSelf: 'flex-start', 
        marginLeft: styles.sectionHeader.marginLeft,
        marginBottom: styles.sectionHeader.marginBottom
      }}>
        <Box
          sx={{
            width: isMobile ? '6px' : '8px',
            height: isMobile ? '6px' : '8px',
            borderRadius: '50%',
            backgroundColor: '#A9C74E',
          }}
        />
        <Typography
          sx={{
            fontFamily: 'Arial',
            fontWeight: 400,
            fontSize: styles.sectionHeader.fontSize,
            lineHeight: styles.sectionHeader.lineHeight,
            letterSpacing: styles.sectionHeader.letterSpacing,
            verticalAlign: 'middle',
            textTransform: 'uppercase',
            color: '#131313',
          }}
        >
          JOIN MONTH
        </Typography>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: styles.title.fontSize,
          lineHeight: styles.title.lineHeight,
          letterSpacing: styles.title.letterSpacing,
          verticalAlign: 'middle',
          textTransform: 'capitalize',
          color: '#1D1D1B',
          paddingLeft: styles.title.paddingLeft,
          alignSelf: 'flex-start',
          marginBottom: styles.title.marginBottom,
          textAlign: 'center'
        }}
      >
        {sectionTitle}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: styles.description.fontSize,
          lineHeight: styles.description.lineHeight,
          letterSpacing: styles.description.letterSpacing,
          verticalAlign: 'middle',
          color: '#7D7D7D',
          maxWidth: isMobile ? '100%' : '1161px',
          textAlign: 'left',
          alignSelf: 'flex-start',
          paddingLeft: styles.description.paddingLeft,
          marginBottom: styles.description.marginBottom
        }}
      >
        {sectionDescription}
      </Typography>

      {/* Donation Cards - Mobile/Medium Slider or Desktop/Tablet Grid */}
      {monthlyDonationsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      ) : monthlyDonations.length === 0 ? (
        <Typography sx={{ textAlign: 'center', color: '#7D7D7D', padding: '40px' }}>
          No monthly donation programs available at the moment.
        </Typography>
      ) : (isMobile || isMediumScreen) ? (
        // Mobile & Medium Screen Slider (600-835px with scroll)
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            gap: isMobile ? '16px' : '18px',
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '10px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}
        >
          {monthlyDonations.map((card) => (
            <Box key={card.id} sx={{ flexShrink: 0 }}>
              <MonthlyDonationProgramCard 
                {...card} 
                isMobile={isMobile} 
                isMediumScreen={isMediumScreen}
              />
            </Box>
          ))}
        </Box>
      ) : (
        // Desktop/Tablet Grid (835px+ with grid)
        <Grid 
          container 
          spacing={isTablet ? "16px" : "20px"} 
          justifyContent="center" 
          sx={{ 
            width: '100%', 
            padding: isTablet ? '0 12px 0 12px' : '0 15px 0 15px' 
          }}
        >
          {monthlyDonations.map((card) => (
            <Grid item key={card.id}>
              <MonthlyDonationProgramCard 
                {...card} 
                isMobile={isMobile} 
                isTablet={isTablet}
                isDesktop={isDesktop}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default MonthlyDonationProgram;
